import { useEffect, useRef } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import type { TopicsRawData } from "../../../api-client/demandSearchTrendsTopicsAPI";

type ActiveListItemProps = {
    isActive: boolean;
};

const CategoryListItemName = styled.span<ActiveListItemProps>`
    ${setFontVariant("primaryItem", colorsPalettes.carbon[500])};
    text-transform: capitalize;
    color: ${({ isActive }) => (isActive ? colorsPalettes.blue[400] : colorsPalettes.carbon[500])};
`;

const CategoryListItemIcon = styled(SWReactIcons)<ActiveListItemProps>`
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    path {
        fill: ${({ isActive }) => (isActive ? colorsPalettes.blue[400] : colorsPalettes.carbon[200])};
    }
`;

const CategoryListItemChevronIcon = styled(SWReactIcons)<ActiveListItemProps>`
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;

    path {
        fill: ${({ isActive }) => (isActive ? colorsPalettes.blue[400] : colorsPalettes.carbon[200])};
    }
`;

export const CATEGORY_LIST_WIDTH = 320;

const CategoryListItem = styled.div<ActiveListItemProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 12px 4px 12px 12px;
    align-self: stretch;
    border-right: 1px solid ${colorsPalettes.carbon[50]};
    border-bottom: 1px solid ${colorsPalettes.carbon[50]};

    background-color: ${({ isActive }) => (isActive ? colorsPalettes.blue[50] : "transparent")};

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background-color: ${colorsPalettes.blue[50]};

        ${CategoryListItemName} {
            color: ${colorsPalettes.blue[400]};
        }

        ${CategoryListItemIcon} {
            path {
                fill: ${colorsPalettes.blue[400]};
            }
        }

        ${CategoryListItemChevronIcon} {
            path {
                fill: ${colorsPalettes.blue[400]};
            }
        }
    }
`;

const TopicsCategoryListContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    width: ${CATEGORY_LIST_WIDTH}px;
    height: 100%;

    scrollbar-width: thin;
`;

type TopicsDiscoverAllTopicsModalMainSectionCategoriesProps = {
    data: TopicsRawData;
    activeCategoryName: string;
    setActiveCategoryName: (topicName: string) => void;
};

export const TopicsDiscoverAllTopicsModalMainSectionCategories = ({
    data,
    activeCategoryName,
    setActiveCategoryName,
}: TopicsDiscoverAllTopicsModalMainSectionCategoriesProps) => {
    const activeItemRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (activeItemRef.current && containerRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const itemRect = activeItemRef.current.getBoundingClientRect();

            const containerHeight = containerRect.height;
            const itemTop = itemRect.top - containerRect.top;
            const itemHeight = itemRect.height;

            const targetScroll = itemTop - containerHeight / 2 + itemHeight / 2;

            containerRef.current.scrollTo({
                top: targetScroll,
                behavior: "smooth",
            });
        }
    }, []);

    return (
        <TopicsCategoryListContainer ref={containerRef}>
            {Object.entries(data).map(([categoryName, category]) => {
                const isActive = categoryName === activeCategoryName;

                return (
                    <CategoryListItem
                        key={categoryName}
                        isActive={isActive}
                        ref={isActive ? activeItemRef : null}
                        onMouseEnter={() => setActiveCategoryName(categoryName)}
                    >
                        <Flex
                            gap="8px"
                            alignItems="center"
                        >
                            <CategoryListItemIcon
                                iconName={category?.Favicon}
                                isActive={isActive}
                            />
                            <CategoryListItemName isActive={isActive}>{category.Name}</CategoryListItemName>
                        </Flex>
                        <CategoryListItemChevronIcon
                            size="xs"
                            iconName={"chev-right"}
                            isActive={isActive}
                        />
                    </CategoryListItem>
                );
            })}
        </TopicsCategoryListContainer>
    );
};
